import React, { useEffect, useMemo, useState } from "react";
import {
  Barcode, CheckCircle, DownloadSimple, FloppyDisk, Printer,
  Tag, WarningCircle,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { useI18n, useT } from "../lib/i18n";

const GROUPS = ["identity", "udi", "handling", "regulatory", "other"];
const DATE_FIELDS = new Set(["manufacture_date", "expiry_date"]);
const TEXTAREA_FIELDS = new Set([
  "manufacturer_address", "intended_purpose", "authorized_representative",
  "importer", "manufacturer_contact", "udi_pi_format", "storage_conditions",
  "handling_conditions", "warnings", "precautions", "contraindications",
  "symbols", "additional_info",
]);

function variantLabel(variant) {
  if (typeof variant === "string") return variant.trim();
  if (!variant || typeof variant !== "object") return "";
  return String(variant.name || variant.model || variant.label || variant.variant || "").trim();
}

function readableAssessmentStatus(assessment, t) {
  const status = String(assessment?.status || assessment?.status_key || assessment?.key || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  const knownStatuses = new Set([
    "required", "conditional", "not_applicable", "insufficient_information",
  ]);
  return knownStatuses.has(status)
    ? t(`labels.assessment.status.${status}`)
    : status.replace(/_/g, " ");
}

function missingCharacteristicLabel(characteristic) {
  if (typeof characteristic === "string") return characteristic;
  if (!characteristic || typeof characteristic !== "object") return String(characteristic ?? "");
  return String(
    characteristic.label || characteristic.name || characteristic.characteristic
    || characteristic.key || characteristic.id || ""
  );
}

function downloadBlob(data, filename) {
  const url = URL.createObjectURL(data);
  const anchor = window.document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function localPending(fields) {
  return (fields || []).reduce((count, field) => {
    const hasValue = Boolean((field.value || "").trim());
    if (field.requirement === "required") {
      return count + (!hasValue || !field.confirmed ? 1 : 0);
    }
    if (field.requirement === "conditional") {
      if (field.applicability === "unknown") return count + 1;
      if (field.applicability === "not_applicable") {
        return count + (!(field.note || "").trim() || !field.confirmed ? 1 : 0);
      }
      if (field.applicability === "applicable") {
        return count + (!hasValue || !field.confirmed ? 1 : 0);
      }
      return count + 1;
    }
    if (field.requirement === "optional" && hasValue && !field.confirmed) return count + 1;
    return count;
  }, 0);
}

export default function DeviceLabelPanel({ device }) {
  const t = useT();
  const { lang } = useI18n();
  const did = device?.id;
  const deviceVariants = useMemo(
    () => [...new Set((device?.variants || []).map(variantLabel).filter(Boolean))],
    [device?.variants]
  );
  const [labels, setLabels] = useState([]);
  const [label, setLabel] = useState(null);
  const [variant, setVariant] = useState("");
  const [labelLang, setLabelLang] = useState(lang);
  const [layout, setLayout] = useState("compact");
  const [widthMm, setWidthMm] = useState(100);
  const [heightMm, setHeightMm] = useState(150);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [attestReviewed, setAttestReviewed] = useState(false);
  const [reviewNote, setReviewNote] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [newDraft, setNewDraft] = useState(false);

  useEffect(() => {
    if (!did) return;
    api.get(`/devices/${did}/labels`)
      .then(async ({ data }) => {
        const rows = data || [];
        setLabels(rows);
        if (rows.length) {
          const latest = await api.get(`/devices/${did}/labels/${rows[0].id}`);
          setLabel(latest.data);
          setVariant(latest.data.variant || "");
          setLabelLang(latest.data.lang || "es");
          setLayout(latest.data.layout || "compact");
          setWidthMm(Number(latest.data.width_mm) || 100);
          setHeightMm(Number(latest.data.height_mm) || 150);
          setExpanded(true);
        }
      })
      .catch((e) => setError(formatApiError(e.response?.data?.detail)));
  }, [did]);

  useEffect(() => {
    if (!label && !newDraft) setLabelLang(lang);
  }, [lang, label, newDraft]);

  function applyVersion(data) {
    setLabel(data);
    setVariant(data.variant || "");
    setLabelLang(data.lang || lang);
    setLayout(data.layout || "compact");
    setWidthMm(Number(data.width_mm) || 100);
    setHeightMm(Number(data.height_mm) || 150);
    setDirty(false);
    setAttestReviewed(false);
    setReviewNote("");
    setError("");
    setNewDraft(false);
  }

  async function refreshHistory() {
    const { data } = await api.get(`/devices/${did}/labels`);
    setLabels(data || []);
  }

  async function createDraft() {
    setBusy("create");
    setError("");
    setNotice("");
    try {
      const { data } = await api.post(`/devices/${did}/labels`, {
        lang: labelLang,
        variant,
        layout,
        width_mm: Number(widthMm),
        height_mm: Number(heightMm),
      });
      applyVersion(data);
      setExpanded(true);
      await refreshHistory();
      setNotice(t("labels.created_notice"));
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  }

  async function selectLabel(id) {
    if (dirty && !window.confirm(t("labels.discard_changes"))) return;
    setBusy("load");
    setError("");
    try {
      const { data } = await api.get(`/devices/${did}/labels/${id}`);
      applyVersion(data);
      setExpanded(true);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  }

  function markDirty(next) {
    setLabel(next);
    setDirty(true);
    setAttestReviewed(false);
  }

  function patchField(fieldId, key, value) {
    if (!label) return;
    const current = label.fields.find((item) => item.id === fieldId);
    const nextField = { ...current, [key]: value };
    if (key === "value") {
      nextField.source = "user_input";
      nextField.confirmed = false;
    }
    if (key === "applicability") {
      nextField.confirmed = false;
      if (value === "not_applicable") {
        nextField.value = "";
        nextField.note = "";
        nextField.source = "user_input";
      }
    }
    if (key === "note") nextField.confirmed = false;
    markDirty({
      ...label,
      fields: label.fields.map((item) => item.id === fieldId ? nextField : item),
    });
  }

  function patchSettings(key, value) {
    if (!label) return;
    markDirty({ ...label, [key]: value });
  }

  async function persistDraft() {
    const { data } = await api.put(`/devices/${did}/labels/${label.id}`, {
      fields: label.fields,
      layout,
      width_mm: Number(widthMm),
      height_mm: Number(heightMm),
    });
    applyVersion(data);
    await refreshHistory();
    return data;
  }

  async function saveDraft() {
    setBusy("save");
    setError("");
    setNotice("");
    try {
      await persistDraft();
      setNotice(t("labels.saved"));
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  }

  async function downloadPdf(labelId, version) {
    setBusy("download");
    setError("");
    try {
      const { data } = await api.get(`/devices/${did}/labels/${labelId}/pdf`, {
        responseType: "blob",
      });
      const safeName = (device.name || "device").replace(/[^\w-]+/g, "_");
      downloadBlob(data, `Etiqueta_${safeName}_v${version || label?.version || 1}.pdf`);
      return true;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
      return false;
    } finally {
      setBusy("");
    }
  }

  async function createPdf() {
    setBusy("pdf");
    setError("");
    setNotice("");
    try {
      const target = dirty ? await persistDraft() : label;
      const { data } = await api.post(`/devices/${did}/labels/${target.id}/pdf`);
      applyVersion(data);
      await refreshHistory();
      if (await downloadPdf(data.id, data.version)) setNotice(t("labels.pdf_created"));
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  }

  async function recordReview() {
    if (!label || !attestReviewed) return;
    if (dirty || !label.pdf_name) {
      setError(t("labels.review_requires_pdf"));
      return;
    }
    setBusy("review");
    setError("");
    setNotice("");
    try {
      const { data } = await api.put(`/devices/${did}/labels/${label.id}/review`, {
        confirmed: true,
        review_note: reviewNote,
      });
      applyVersion(data);
      await refreshHistory();
      setNotice(t("labels.reviewed"));
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  }

  function startAnotherDraft() {
    if (dirty && !window.confirm(t("labels.discard_changes"))) return;
    setNewDraft(true);
    setVariant("");
    setError("");
    setNotice("");
  }

  function cancelNewDraft() {
    setNewDraft(false);
    setVariant(label?.variant || "");
    setLabelLang(label?.lang || lang);
    setLayout(label?.layout || "compact");
    setWidthMm(Number(label?.width_mm) || 100);
    setHeightMm(Number(label?.height_mm) || 150);
  }

  const pendingCount = label
    ? localPending(label.fields) + (
      label.fields.some((field) => field.id === "udi_di" && field.applicability === "applicable")
      && (dirty || (label.checklist?.barcode?.status && label.checklist.barcode.status !== "ready"))
        ? 1 : 0
    )
    : 0;

  return (
    <section id="device-label-generator" className="mt-7 border border-cyan-200 bg-white shadow-sm" data-testid="device-label-panel">
      <div className="flex flex-col gap-3 border-b border-cyan-100 bg-cyan-50/60 p-4 sm:flex-row sm:items-start sm:justify-between sm:p-5">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2 text-[10px] font-mono font-bold uppercase tracking-widest text-cyan-900">
            <Tag size={14} /> {t("labels.kicker")}
          </div>
          <h2 className="mt-1 text-lg font-bold text-zinc-900">{t("labels.title")}</h2>
          <p className="mt-1 max-w-3xl text-xs leading-5 text-zinc-600">{t("labels.subtitle")}</p>
        </div>
        {label && (
          <span className={`inline-flex shrink-0 items-center gap-1 border px-2 py-1 text-[10px] font-bold uppercase tracking-wide ${label.status === "reviewed" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
            {label.status === "reviewed" ? <CheckCircle size={12} /> : <WarningCircle size={12} />}
            {label.status === "reviewed" ? t("labels.status_reviewed") : t("labels.status_draft")}
          </span>
        )}
      </div>

      <div className="space-y-5 p-4 sm:p-5">
        <div className="border-l-4 border-amber-400 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-950">
          <strong>{t("labels.safety_title")}</strong> {t("labels.safety")}
        </div>

        {(!label || newDraft) && (
          <div className="grid gap-4 border border-zinc-200 p-3 sm:grid-cols-2 sm:p-4">
            <label className="block text-xs">
              <span className="mb-1 block font-bold uppercase tracking-wide text-zinc-600">{t("labels.variant")}</span>
              <select value={variant} onChange={(event) => setVariant(event.target.value)}
                className="w-full border border-zinc-200 bg-white px-2 py-2">
                <option value="">{t("labels.device_level")}</option>
                {deviceVariants.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
            </label>
            <div className="block text-xs">
              <span className="mb-1 block font-bold uppercase tracking-wide text-zinc-600">{t("labels.layout")}</span>
              <select value={layout} onChange={(event) => setLayout(event.target.value)}
                className="w-full border border-zinc-200 bg-white px-2 py-2">
                <option value="compact">{t("labels.layout_compact")}</option>
                <option value="two_column">{t("labels.layout_two_column")}</option>
              </select>
            </div>
            <label className="block text-xs">
              <span className="mb-1 block font-bold uppercase tracking-wide text-zinc-600">{t("labels.language")}</span>
              <select value={labelLang} onChange={(event) => setLabelLang(event.target.value)}
                className="w-full border border-zinc-200 bg-white px-2 py-2">
                <option value="es">Español</option>
                <option value="en">English</option>
              </select>
            </label>
            <label className="block text-xs">
              <span className="mb-1 block font-bold uppercase tracking-wide text-zinc-600">{t("labels.width")}</span>
              <input type="number" min="60" max="300" step="1" value={widthMm}
                onChange={(event) => setWidthMm(event.target.value)}
                className="w-full border border-zinc-200 px-2 py-2" />
            </label>
            <label className="block text-xs">
              <span className="mb-1 block font-bold uppercase tracking-wide text-zinc-600">{t("labels.height")}</span>
              <input type="number" min="40" max="400" step="1" value={heightMm}
                onChange={(event) => setHeightMm(event.target.value)}
                className="w-full border border-zinc-200 px-2 py-2" />
            </label>
            <div className="sm:col-span-2">
              <button type="button" onClick={createDraft} disabled={busy !== ""}
                className="inline-flex items-center gap-2 bg-cyan-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-cyan-900 disabled:opacity-50"
                data-testid="device-label-create">
                <Tag size={15} /> {busy === "create" ? t("labels.creating") : t("labels.create")}
              </button>
              {label && newDraft && (
                <button type="button" onClick={cancelNewDraft}
                  className="ml-2 border border-zinc-300 px-3 py-2.5 text-xs font-semibold text-zinc-700">
                  {t("labels.cancel")}
                </button>
              )}
            </div>
          </div>
        )}

        {(error || notice) && (
          <div role={error ? "alert" : "status"} className={`border px-3 py-2 text-xs ${error ? "border-red-200 bg-red-50 text-red-800" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}>
            {error || notice}
          </div>
        )}

        {labels.length > 0 && (
          <div className="border border-zinc-200 p-3 sm:p-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-xs font-bold uppercase tracking-wide text-zinc-800">{t("labels.history")}</h3>
              <div className="flex flex-wrap items-center gap-3">
                {label && !newDraft && (
                  <button type="button" onClick={startAnotherDraft}
                    className="text-xs font-semibold text-cyan-800 hover:underline">
                    {t("labels.new_draft")}
                  </button>
                )}
                <button type="button" onClick={() => setExpanded((value) => !value)}
                  className="text-xs font-semibold text-cyan-800 hover:underline">
                  {expanded ? t("labels.hide_editor") : t("labels.show_editor")}
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {labels.map((item) => (
                <div key={item.id} className={`flex items-center gap-1 border px-2 py-1.5 text-xs ${label?.id === item.id ? "border-cyan-300 bg-cyan-50" : "border-zinc-200 bg-white"}`}>
                  <button type="button" onClick={() => selectLabel(item.id)} disabled={busy !== ""}
                    className="text-left font-semibold text-zinc-800 disabled:opacity-50">
                    v{item.version} · {item.variant || t("labels.device_level")} · {item.status === "reviewed" ? t("labels.status_reviewed") : t("labels.status_draft")}
                  </button>
                  {item.pdf_name && (
                    <button type="button" onClick={() => downloadPdf(item.id, item.version)} disabled={busy !== ""}
                      title={t("labels.download")} className="ml-1 p-1 text-cyan-800 hover:bg-cyan-100 disabled:opacity-40">
                      <DownloadSimple size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {label && expanded && !newDraft && (
          <div className="space-y-4" data-testid="device-label-editor">
            <div className="flex flex-col gap-2 border-b border-zinc-200 pb-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-sm font-bold text-zinc-900">{t("labels.editor")} · v{label.version}</h3>
                <p className="text-[10px] text-zinc-500">{t("labels.created")} {new Date(label.created_at).toLocaleString()}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={saveDraft} disabled={busy !== "" || !dirty}
                  className="inline-flex items-center gap-1 border border-zinc-300 px-3 py-2 text-xs font-semibold text-zinc-700 disabled:opacity-40">
                  <FloppyDisk size={13} /> {busy === "save" ? t("labels.saving") : t("labels.save_version")}
                </button>
                <button type="button" onClick={createPdf} disabled={busy !== ""}
                  className="inline-flex items-center gap-1 border border-cyan-200 bg-cyan-50 px-3 py-2 text-xs font-semibold text-cyan-900 disabled:opacity-40">
                  <Printer size={13} /> {busy === "pdf" || busy === "download" ? t("labels.creating_pdf") : t("labels.create_download")}
                </button>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block border border-zinc-200 p-3">
                <span className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-600">{t("labels.layout")}</span>
                <select value={layout} onChange={(event) => patchSettings("layout", event.target.value)}
                  className="w-full border border-zinc-200 bg-white px-2 py-1.5 text-xs">
                  <option value="compact">{t("labels.layout_compact")}</option>
                  <option value="two_column">{t("labels.layout_two_column")}</option>
                </select>
              </label>
              <div className="grid grid-cols-2 gap-2 border border-zinc-200 p-3">
                <label className="text-xs">
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-600">{t("labels.width")}</span>
                  <input type="number" min="60" max="300" step="1" value={widthMm}
                    onChange={(event) => { setWidthMm(event.target.value); setDirty(true); }}
                    className="w-full border border-zinc-200 px-2 py-1.5" />
                </label>
                <label className="text-xs">
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-600">{t("labels.height")}</span>
                  <input type="number" min="40" max="400" step="1" value={heightMm}
                    onChange={(event) => { setHeightMm(event.target.value); setDirty(true); }}
                    className="w-full border border-zinc-200 px-2 py-1.5" />
                </label>
              </div>
            </div>

            <div className="border border-amber-200 bg-amber-50 p-3">
              <div className="flex items-center justify-between gap-2">
                <h4 className="text-xs font-bold text-amber-950">{t("labels.checklist")}</h4>
                <span className="text-xs font-semibold text-amber-900">{t("labels.pending_count").replace("{count}", String(pendingCount))}</span>
              </div>
              <p className="mt-1 text-[10px] leading-4 text-amber-900">{t("labels.applicability_help")}</p>
              {label.checklist?.barcode?.reason && (
                <p className="mt-2 inline-flex items-start gap-1 text-[10px] leading-4 text-amber-900">
                  <Barcode size={13} className="mt-0.5 shrink-0" /> {label.checklist.barcode.reason}
                </p>
              )}
              {(label.checklist?.warnings || []).map((warning, index) => (
                <p key={index} className="mt-1 text-[10px] leading-4 text-amber-900">{warning}</p>
              ))}
            </div>

            <div className="space-y-3">
              {GROUPS.map((group) => {
                const groupFields = (label.fields || []).filter((field) => field.group === group);
                if (!groupFields.length) return null;
                return (
                  <article key={group} className="border border-zinc-200">
                    <div className="border-b border-zinc-100 bg-zinc-50 px-3 py-2">
                      <h4 className="text-xs font-bold text-zinc-800">{t(`labels.group.${group}`)}</h4>
                    </div>
                    <div className="space-y-3 p-3">
                      {groupFields.map((field) => (
                        <div key={field.id} className="border border-zinc-100 p-2.5">
                          <div className="mb-2 flex flex-wrap items-center gap-2">
                            <span className="text-xs font-semibold text-zinc-800">{field.label}</span>
                            <span className={`border px-1.5 py-0.5 text-[9px] font-bold uppercase ${field.requirement === "required" ? "border-red-200 bg-red-50 text-red-800" : field.requirement === "conditional" ? "border-amber-200 bg-amber-50 text-amber-800" : "border-zinc-200 bg-zinc-50 text-zinc-600"}`}>
                              {t(`labels.requirement.${field.requirement}`)}
                            </span>
                            {field.source && (
                              <span className={`text-[9px] ${field.confirmed ? "text-emerald-700" : "text-zinc-500"}`}>
                                {t(`labels.source.${field.source}${field.confirmed ? "_confirmed" : ""}`)}
                              </span>
                            )}
                          </div>
                          {field.rule_assessment && (
                            <aside className="mb-2 border-l-2 border-cyan-700 bg-cyan-50/70 px-2.5 py-2 text-[10px] leading-4 text-cyan-950">
                              <div className="font-bold uppercase tracking-wide">
                                {readableAssessmentStatus(field.rule_assessment, t)}
                              </div>
                              {field.rule_assessment.basis && (
                                <p className="mt-1">
                                  <span className="font-semibold">{t("labels.assessment_basis")}: </span>
                                  {field.rule_assessment.basis}
                                </p>
                              )}
                              {Array.isArray(field.rule_assessment.missing_characteristics)
                                && field.rule_assessment.missing_characteristics.length > 0 && (
                                <div className="mt-1">
                                  <span className="font-semibold">{t("labels.missing_facts")}: </span>
                                  <ul className="ml-4 list-disc">
                                    {field.rule_assessment.missing_characteristics.map((item, index) => (
                                      <li key={`${field.id}-missing-${index}`}>{missingCharacteristicLabel(item)}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </aside>
                          )}
                          {field.id === "udi_di"
                            && String(label.fields.find((item) => item.id === "udi_issuing_entity")?.value || "")
                              .trim().toUpperCase() === "HIBCC" && (
                            <p className="mb-2 text-[10px] leading-4 text-cyan-900">
                              {t("labels.hibcc_di_help")}
                            </p>
                          )}
                          <div className="grid gap-2 lg:grid-cols-[minmax(0,1fr)_220px]">
                            {TEXTAREA_FIELDS.has(field.id) ? (
                              <textarea rows={field.id === "warnings" || field.id === "additional_info" ? 3 : 2}
                                value={field.value || ""} onChange={(event) => patchField(field.id, "value", event.target.value)}
                                className="w-full border border-zinc-200 px-2 py-1.5 text-xs leading-5 focus:border-cyan-700 focus:outline-none"
                                aria-label={field.label} />
                            ) : (
                              <input type={DATE_FIELDS.has(field.id) ? "date" : "text"}
                                value={field.value || ""} onChange={(event) => patchField(field.id, "value", event.target.value)}
                                disabled={field.id === "language"}
                                className="w-full border border-zinc-200 px-2 py-1.5 text-xs focus:border-cyan-700 focus:outline-none disabled:bg-zinc-50 disabled:text-zinc-500"
                                aria-label={field.label} />
                            )}
                            <div className="space-y-2">
                              {field.requirement === "conditional" && (
                                <label className="block">
                                  <span className="sr-only">{t("labels.applicability")}</span>
                                  <select value={field.applicability}
                                    onChange={(event) => patchField(field.id, "applicability", event.target.value)}
                                    className="w-full border border-zinc-200 bg-white px-2 py-1.5 text-[11px]">
                                    <option value="unknown">{t("labels.app_unknown")}</option>
                                    <option value="applicable">{t("labels.applicable")}</option>
                                    <option value="not_applicable">{t("labels.not_applicable")}</option>
                                  </select>
                                </label>
                              )}
                              {field.applicability_suggestion === "applicable" && field.applicability === "unknown" && (
                                <p className="text-[10px] leading-4 text-cyan-800">{t("labels.suggested_applicable")}</p>
                              )}
                              {field.applicability === "not_applicable" && (
                                <textarea rows={2} value={field.note || ""}
                                  onChange={(event) => patchField(field.id, "note", event.target.value)}
                                  placeholder={t("labels.rationale")}
                                  className="w-full border border-zinc-200 px-2 py-1.5 text-[11px]" />
                              )}
                              <label className="flex items-start gap-1.5 text-[10px] leading-4 text-zinc-600">
                                <input type="checkbox" checked={Boolean(field.confirmed)}
                                  onChange={(event) => patchField(field.id, "confirmed", event.target.checked)}
                                  className="mt-0.5" />
                                <span>{t("labels.confirm_field")}</span>
                              </label>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </article>
                );
              })}
            </div>

            {label.status !== "reviewed" && (
              <div className="border border-zinc-200 p-3">
                <label className="flex items-start gap-2 text-xs leading-5 text-zinc-700">
                  <input type="checkbox" checked={attestReviewed}
                    onChange={(event) => setAttestReviewed(event.target.checked)} className="mt-1" />
                  <span>{t("labels.review_attestation")}</span>
                </label>
                <label className="mt-2 block">
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-500">{t("labels.review_note")}</span>
                  <textarea rows={2} value={reviewNote} onChange={(event) => setReviewNote(event.target.value)}
                    className="w-full border border-zinc-200 px-2 py-1.5 text-xs" />
                </label>
                <button type="button" onClick={recordReview}
                  disabled={busy !== "" || pendingCount > 0 || !attestReviewed || dirty || !label.pdf_name}
                  className="mt-3 inline-flex items-center gap-2 border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800 disabled:opacity-40">
                  <CheckCircle size={14} /> {busy === "review" ? t("labels.reviewing") : t("labels.record_review")}
                </button>
                {pendingCount > 0 && <p className="mt-2 text-[10px] text-amber-800">{t("labels.review_blocked")}</p>}
                {(!label.pdf_name || dirty) && <p className="mt-2 text-[10px] text-zinc-500">{t("labels.review_requires_pdf")}</p>}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
